import React, { useEffect, useRef, useState } from "react";
import api, { mediaUrl, formatApiError } from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Upload, Trash2, Mic, Download, Play, FileText, Video, Youtube, Facebook, Link2, ExternalLink, Pencil, Search, Layers } from "lucide-react";
import { usePaged, Paginator } from "../components/Paginator";
import { SermonNotes, markWatched } from "../components/SermonNotes";
import { SeriesCollections } from "../components/SeriesCollections";
import { NotebookPen, CheckCircle2 } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

const kind = (ct = "") => ct.startsWith("audio") ? "audio" : ct.startsWith("video") ? "video" : "doc";

const youtubeId = (url = "") => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const linkInfo = (url = "") => {
  const yt = youtubeId(url);
  if (yt) return { platform: "youtube", label: "YouTube", embed: `https://www.youtube.com/embed/${yt}` };
  if (/facebook\.com|fb\.watch/i.test(url)) return { platform: "facebook", label: "Facebook", embed: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false` };
  return { platform: "other", label: "Watch", embed: null };
};

export default function Sermons() {
  const { user } = useAuth();
  const isStaff = !!user?.caps?.manage_content;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("file"); // "file" | "link"
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [speakerFilter, setSpeakerFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [seriesFilter, setSeriesFilter] = useState("");
  const [form, setForm] = useState({ title: "", speaker: "", sermon_date: "", description: "", source_url: "", series: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [notesFor, setNotesFor] = useState(null);
  const [watched, setWatched] = useState(new Set());
  const fileRef = useRef();
  useEffect(() => { api.get("/sermons/watched").then(({ data }) => setWatched(new Set(data))).catch(() => {}); }, []);
  const togglePlay = (id) => {
    if (playing === id) return setPlaying(null);
    setPlaying(id); markWatched(id); setWatched((w) => new Set(w).add(id));
  };

  const [syncing, setSyncing] = useState(false);
  const syncYoutube = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sermons/youtube-sync");
      if (data.error || data.skipped) toast.error(data.error || data.skipped);
      else toast.success(data.added ? `${data.added} new sermon${data.added > 1 ? "s" : ""} added from YouTube` : "No new Sunday or Wednesday videos on YouTube");
      load();
    } catch (e) { toast.error("Couldn't sync from YouTube"); }
    finally { setSyncing(false); }
  };
  const load = async () => { const { data } = await api.get("/media", { params: { category: "sermon" } }); setItems(data); };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: "", speaker: "", sermon_date: "", description: "", source_url: "", series: "" }); setFile(null); setMode("file"); setEditing(null); };

  const openEdit = (s) => {
    const isLink = !!s.is_link || s.content_type === "link";
    setEditing(s);
    setMode(isLink ? "link" : "file");
    setForm({ title: s.title || "", speaker: s.speaker || "", sermon_date: s.sermon_date || "", description: s.description || "", source_url: s.source_url || "", series: s.series || "" });
    setFile(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (editing) {
        await api.patch(`/media/${editing.id}`, {
          title: form.title, speaker: form.speaker, sermon_date: form.sermon_date, series: form.series,
          description: form.description, ...(mode === "link" ? { source_url: form.source_url } : {}),
        });
        toast.success("Sermon updated");
      } else if (mode === "link") {
        if (!form.source_url.trim()) { setUploading(false); return toast.error("Please paste a YouTube, Facebook or other link"); }
        await api.post("/media/link", {
          category: "sermon", title: form.title, speaker: form.speaker,
          sermon_date: form.sermon_date, description: form.description, source_url: form.source_url, series: form.series,
        });
        toast.success("Sermon link added");
      } else {
        if (!file) { setUploading(false); return toast.error("Please choose an audio, video or PDF file"); }
        const fd = new FormData();
        fd.append("file", file); fd.append("category", "sermon");
        fd.append("title", form.title || file.name); fd.append("speaker", form.speaker);
        fd.append("sermon_date", form.sermon_date); fd.append("description", form.description);
        fd.append("series", form.series);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Sermon uploaded");
      }
      setOpen(false); resetForm(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const del = (id) => { const it = items.find((x) => x.id === id); setItems((l) => l.filter((x) => x.id !== id)); undoableDelete({ label: it?.title || "Sermon", entityType: "sermon", perform: () => api.delete(`/media/${id}`), onUndo: load }); };

  const q = search.trim().toLowerCase();
  const speakers = [...new Set(items.map((s) => (s.speaker || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const seriesList = [...new Set(items.map((s) => (s.series || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const hasFilters = !!(q || speakerFilter || dateFrom || dateTo || seriesFilter);
  const clearFilters = () => { setSearch(""); setSpeakerFilter(""); setDateFrom(""); setDateTo(""); setSeriesFilter(""); };
  const filtered = items
    .filter((s) => !q || [s.title, s.speaker, s.description, s.series].some((v) => (v || "").toLowerCase().includes(q)))
    .filter((s) => !speakerFilter || (s.speaker || "").trim() === speakerFilter)
    .filter((s) => !seriesFilter || (s.series || "").trim() === seriesFilter)
    .filter((s) => !dateFrom || (s.sermon_date && s.sermon_date >= dateFrom))
    .filter((s) => !dateTo || (s.sermon_date && s.sermon_date <= dateTo))
    .sort((a, b) => {
      const da = a.sermon_date || "", dbb = b.sermon_date || "";
      // When following a series, always show oldest → newest so it plays in order
      const dir = seriesFilter ? "asc" : sortDir;
      if (!da && !dbb) return 0;
      if (!da) return 1;
      if (!dbb) return -1;
      if (da === dbb) return 0;
      return dir === "desc" ? (da < dbb ? 1 : -1) : (da < dbb ? -1 : 1);
    });
  const { page, setPage, pageCount, pageItems } = usePaged(filtered, 9);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Sermon Library</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Listen, watch and download past messages</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-text-3)" }} />
            <input data-testid="sermon-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sermons..." className="bg-white border border-[#EBE9E4] rounded-full focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all pl-9 pr-4 py-2.5 text-sm w-56" />
          </div>
          {isStaff && (
            <button data-testid="youtube-sync-button" onClick={syncYoutube} disabled={syncing} className="flex items-center gap-2 rounded-full px-4 py-2.5 font-medium border bg-white hover:bg-[#E3F1EC] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
              <Youtube className="h-5 w-5" /> {syncing ? "Checking YouTube…" : "Sync from YouTube"}
            </button>
          )}
          {isStaff && (
            <button data-testid="add-sermon-button" onClick={() => { resetForm(); setOpen(true); }} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-medium shadow-sm hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
              <Upload className="h-5 w-5" /> Add Sermon
            </button>
          )}
        </div>
      </div>

      <SeriesCollections />

      <div className={`${CARD} p-3 flex flex-wrap items-center gap-3`} data-testid="sermon-filter-bar">
        <select data-testid="sermon-speaker-filter" value={speakerFilter} onChange={(e) => setSpeakerFilter(e.target.value)} className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
          <option value="">All speakers</option>
          {speakers.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
        </select>
        {seriesList.length > 0 && (
          <select data-testid="sermon-series-filter" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)} className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
            <option value="">All series</option>
            {seriesList.map((sr) => <option key={sr} value={sr}>{sr}</option>)}
          </select>
        )}
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--sj-text-2)" }}>
          <span>From</span>
          <input data-testid="sermon-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20" />
          <span>To</span>
          <input data-testid="sermon-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20" />
        </div>
        <select data-testid="sermon-sort" value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>
        {hasFilters && (
          <button data-testid="sermon-clear-filters" onClick={clearFilters} className="text-sm font-medium rounded-full px-4 py-2 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>Clear filters</button>
        )}
        <span className="ml-auto text-xs" style={{ color: "var(--sj-text-3)" }} data-testid="sermon-result-count">{filtered.length} sermon{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {filtered.length === 0 ? (
        <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}>
          <Mic className="h-12 w-12" /> {hasFilters ? "No sermons match your search." : "No sermons uploaded yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sermons-grid">
          {pageItems.map((s) => {
            const isLink = !!s.is_link || s.content_type === "link";
            const li = isLink ? linkInfo(s.source_url || "") : null;
            const k = kind(s.content_type);
            const Icon = isLink
              ? (li.platform === "youtube" ? Youtube : li.platform === "facebook" ? Facebook : Link2)
              : (k === "audio" ? Mic : k === "video" ? Video : FileText);
            const canEmbed = isLink && !!li.embed;
            return (
              <div key={s.id} data-testid={`sermon-${s.id}`} className={`${CARD} p-5 flex flex-col`}>
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#0B5E4E1A" }}><Icon className="h-6 w-6" style={{ color: "#0B5E4E" }} /></div>
                  <div className="flex items-center gap-2">
                    {isLink && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full" style={{ background: "#0B5E4E1A", color: "#0B5E4E" }}>{li.label}</span>}
                    {isStaff && <button data-testid={`sermon-edit-${s.id}`} onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Pencil className="h-4 w-4" /></button>}
                    {isStaff && <button data-testid={`sermon-delete-${s.id}`} onClick={() => del(s.id)} className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
                <h3 className="mt-3 font-head text-lg font-semibold leading-tight">{s.title}</h3>
                {watched.has(s.id) && <span data-testid={`sermon-watched-${s.id}`} className="mt-1 self-start inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "var(--sj-secondary)" }}><CheckCircle2 className="h-3 w-3" /> Watched</span>}
                <div className="text-xs mt-1" style={{ color: "var(--sj-text-2)" }}>{s.speaker || "Streams of Joy"}{s.sermon_date ? ` · ${s.sermon_date}` : ""}</div>
                {s.series && <button data-testid={`sermon-series-tag-${s.id}`} onClick={() => setSeriesFilter(s.series)} className="mt-1.5 self-start inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full hover:brightness-95" style={{ background: "#17A88A1A", color: "#084438" }}><Layers className="h-3 w-3" /> {s.series}</button>}
                {s.description && <p className="text-sm mt-2 line-clamp-2" style={{ color: "var(--sj-text-2)" }}>{s.description}</p>}

                {playing === s.id && !isLink && k === "audio" && <audio src={mediaUrl(s.id)} controls autoPlay className="mt-3 w-full" />}
                {playing === s.id && !isLink && k === "video" && <video src={mediaUrl(s.id)} controls autoPlay className="mt-3 w-full rounded-lg" />}
                {playing === s.id && canEmbed && (
                  <div className="mt-3 w-full rounded-lg overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                    <iframe title={s.title} src={li.embed} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {isLink ? (
                    <>
                      {canEmbed && (
                        <button data-testid={`sermon-play-${s.id}`} onClick={() => togglePlay(s.id)} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}>
                          <Play className="h-3.5 w-3.5" /> {playing === s.id ? "Stop" : "Play"}
                        </button>
                      )}
                      <a data-testid={`sermon-watch-${s.id}`} href={s.source_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                        <ExternalLink className="h-3.5 w-3.5" /> Watch on {li.label}
                      </a>
                    </>
                  ) : (
                    <>
                      {k !== "doc" && (
                        <button data-testid={`sermon-play-${s.id}`} onClick={() => togglePlay(s.id)} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}>
                          <Play className="h-3.5 w-3.5" /> {playing === s.id ? "Stop" : "Play"}
                        </button>
                      )}
                      <a data-testid={`sermon-download-${s.id}`} href={mediaUrl(s.id)} target="_blank" rel="noreferrer" download className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                        <Download className="h-3.5 w-3.5" /> {k === "doc" ? "Open / Download" : "Download"}
                      </a>
                    </>
                  )}
                  <button data-testid={`sermon-notes-toggle-${s.id}`} onClick={() => setNotesFor(notesFor === s.id ? null : s.id)} className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
                    <NotebookPen className="h-3.5 w-3.5" /> Notes
                  </button>
                </div>
                {notesFor === s.id && <SermonNotes sermonId={s.id} compact />}
              </div>
            );
          })}
        </div>
      )}
      <Paginator page={page} pageCount={pageCount} onPage={setPage} testid="sermons-paginator" />

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">{editing ? "Edit Sermon" : "Add Sermon"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            {!editing && (
            <div className="flex gap-2 p-1 rounded-full" style={{ background: "#EDF2F4" }}>
              <button type="button" data-testid="sermon-mode-file" onClick={() => setMode("file")} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full py-2 transition-colors" style={mode === "file" ? { background: "#fff", color: "var(--sj-primary)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" } : { color: "var(--sj-text-2)" }}>
                <Upload className="h-4 w-4" /> Upload file
              </button>
              <button type="button" data-testid="sermon-mode-link" onClick={() => setMode("link")} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full py-2 transition-colors" style={mode === "link" ? { background: "#fff", color: "var(--sj-primary)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" } : { color: "var(--sj-text-2)" }}>
                <Link2 className="h-4 w-4" /> Add a link
              </button>
            </div>
            )}

            <input data-testid="sermon-title-input" required placeholder="Sermon title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="sermon-speaker-input" placeholder="Speaker" value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} className={inputCls} />
              <input type="date" value={form.sermon_date} onChange={(e) => setForm({ ...form, sermon_date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <input data-testid="sermon-series-input" list="sermon-series-options" placeholder="Series / playlist (optional)" value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} className={inputCls} />
              <datalist id="sermon-series-options">{seriesList.map((sr) => <option key={sr} value={sr} />)}</datalist>
              <p className="text-xs mt-1.5" style={{ color: "var(--sj-text-3)" }}>Group sermons into a series so members can follow a teaching series end to end.</p>
            </div>
            <textarea placeholder="Description (optional)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />

            {mode === "link" ? (
              <div>
                <input data-testid="sermon-link-input" placeholder="Paste a YouTube, Facebook or other link" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className={inputCls} />
                <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: "var(--sj-text-3)" }}>
                  <Youtube className="h-3.5 w-3.5" /> YouTube &amp; <Facebook className="h-3.5 w-3.5" /> Facebook links play inline. Any other link opens in a new tab.
                </p>
              </div>
            ) : editing ? (
              <p className="text-xs flex items-center gap-1.5" style={{ color: "var(--sj-text-3)" }}>
                <FileText className="h-3.5 w-3.5" /> Editing details for the uploaded file{editing.filename ? ` "${editing.filename}"` : ""}. To change the file itself, delete this sermon and upload again.
              </p>
            ) : (
              <div>
                <input ref={fileRef} data-testid="sermon-file-input" type="file" accept="audio/*,video/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} className="hidden" />
                <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 rounded-xl py-3 border-2 border-dashed" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  <Upload className="h-5 w-5" /> {file ? file.name : "Choose audio / video / PDF"}
                </button>
              </div>
            )}

            <DialogFooter>
              <button type="button" onClick={() => { setOpen(false); resetForm(); }} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" disabled={uploading} data-testid="save-sermon-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{uploading ? "Saving..." : (editing ? "Save Changes" : (mode === "link" ? "Add Link" : "Upload Sermon"))}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
